/**
 * Casio fx-991EX ClassWiz — math.js powered evaluator
 * 
 * Converts MathQuill LaTeX → math.js expression string → exact result.
 * Produces fraction, mixed-fraction, pi-fraction, and surd outputs.
 * Supports summation, numerical derivatives, numerical integrals,
 * nPr, nCr, Pol/Rec, DMS, random, and unit conversions.
 */
import { create, all } from 'mathjs';
import Fraction from 'fraction.js';

// ── math.js instance with bignumber support ──
const math = create(all, { number: 'BigNumber', precision: 64 });

// ── Angle mode ──
export const ANGLE = { DEG: 'DEG', RAD: 'RAD', GRAD: 'GRAD' };

// ══════════════════════════════════════════════
//  LaTeX → math.js expression string
// ══════════════════════════════════════════════

export function latexToExpr(latex) {
  let t = latex;

  // ── recurring decimals: \overline{...} ──
  const recurPat = /(?:(\d+))?\.?(\d*)?\\overline\{(\d+)\}/g;
  t = t.replace(recurPat, (_, whole, dec, rep) => {
    whole = whole || '0';
    dec = dec || '';
    if (!rep) return _;
    const parts = [];
    if (whole !== '0') parts.push(`(${whole})`);
    if (dec) parts.push(`(${dec}/1${'0'.repeat(dec.length)})`);
    parts.push(`(${rep}/(${'9'.repeat(rep.length)}*1${'0'.repeat(dec.length)}))`);
    return parts.length === 0 ? '0' : `(${parts.join(' + ')})`;
  });

  // ── strip MathQuill delimiters ──
  t = t.replace(/\\left\(/g, '(').replace(/\\right\)/g, ')');
  t = t.replace(/\\left\|/g, 'abs(').replace(/\\right\|/g, ')');

  // ── basic symbols ──
  t = t.replace(/\\pi/g, 'pi');
  t = t.replace(/\\times/g, '*');
  t = t.replace(/\\div/g, '/');
  t = t.replace(/\\cdot/g, '*');
  t = t.replace(/\\%/g, '/100');

  // ── inverse trig + hyperbolic (order matters — longest first) ──
  const trigMap = [
    [/\\sinh\^\{-1\}/g, 'asinh'],
    [/\\cosh\^\{-1\}/g, 'acosh'],
    [/\\tanh\^\{-1\}/g, 'atanh'],
    [/\\sin\^\{-1\}/g, 'asin'],
    [/\\cos\^\{-1\}/g, 'acos'],
    [/\\tan\^\{-1\}/g, 'atan'],
    [/\\sinh/g, 'sinh'],
    [/\\cosh/g, 'cosh'],
    [/\\tanh/g, 'tanh'],
    [/\\sin/g, 'sin'],
    [/\\cos/g, 'cos'],
    [/\\tan/g, 'tan'],
  ];
  for (const [re, fn] of trigMap) t = t.replace(re, fn);

  // ── summation: \sum_{var=start}^{end} body ──
  // MathQuill produces:  \sum _{x=1}^{10}x^{2}
  t = t.replace(/\\sum\s*_\{([a-zA-Z])=([^{}]*)\}\^\{([^{}]*)\}\s*\{?([^{}]+)\}?/g,
    'casioSum($4, $1, $2, $3)');
  // fallback simpler form
  t = t.replace(/\\sum\s*_\{([a-zA-Z])=([^{}]*)\}\^\{([^{}]*)\}/g,
    'casioSum($1, $1, $2, $3)');

  // ── definite integral: \int_{a}^{b} body \, dx or \int_{a}^{b} body ──
  t = t.replace(/\\int\s*_\{([^{}]*)\}\^\{([^{}]*)\}\s*\{?([^{}]+)\}?\s*(?:\\,?\s*d([a-zA-Z]))?/g,
    'casioIntegral($3, $1, $2)');

  // ── derivative: \frac{d}{dx}(...)|_{x=a}  or  casioDerivTemplate(expr, point) ──
  t = t.replace(/\\frac\{d\}\{d([a-zA-Z])\}\s*\(([^)]+)\)\s*(?:\|_\{[a-zA-Z]=([^{}]*)\})?/g,
    (_, v, expr, pt) => pt ? `casioDeriv(${expr}, ${v}, ${pt})` : `casioDeriv(${expr}, ${v}, ${v})`);

  // ── fractions, roots, powers (iterate until stable) ──
  let prev;
  do {
    prev = t;
    // mixed fractions: <integer>\frac{num}{den}
    t = t.replace(/(\(.*?\)|[^+\-*/^()\s\\{}_]+)(?:\s+|\\ )?\\frac\{([^{}]*)\}\{([^{}]*)\}/g,
      '(($1) + (($2)/($3)))');
    // plain fractions
    t = t.replace(/\\frac\{([^{}]*)\}\{([^{}]*)\}/g, '(($1)/($2))');
    // nth root
    t = t.replace(/\\sqrt\[([^{}]*)\]\{([^{}]*)\}/g, 'nthRoot($2, $1)');
    // square root
    t = t.replace(/\\sqrt\{([^{}]*)\}/g, 'sqrt($1)');
    // exponents
    t = t.replace(/\^\{([^{}]*)\}/g, '^($1)');
  } while (prev !== t);

  // ── log with base ──
  t = t.replace(/\\log_\{(.+?)\}\((.*?)\)/g, 'log($2, $1)');
  t = t.replace(/\\log_\{(.+?)\}\s*([0-9a-zA-Z.]+)/g, 'log($2, $1)');
  t = t.replace(/\\log_([0-9a-zA-Z.]+)\((.*?)\)/g, 'log($2, $1)');
  t = t.replace(/\\log_([0-9a-zA-Z.]+)\s*([0-9a-zA-Z.]+)/g, 'log($2, $1)');

  // ── ln → log, log → log10 ──
  t = t.replace(/\\ln\((.*?)\)/g, 'log($1)');
  t = t.replace(/\\ln\s*([0-9a-zA-Z.]+)/g, 'log($1)');
  t = t.replace(/\\log\((.*?)\)/g, 'log10($1)');
  t = t.replace(/\\log\s*([0-9a-zA-Z.]+)/g, 'log10($1)');

  // ── abs ──
  t = t.replace(/abs\(/g, 'abs(');

  // ── nPr / nCr ──
  t = t.replace(/nPr\(/g, 'permutations(');
  t = t.replace(/nCr\(/g, 'combinations(');

  // ── Pol/Rec ──
  t = t.replace(/Pol\(/g, 'pol(');
  t = t.replace(/Rec\(/g, 'rec(');

  // ── random ──
  t = t.replace(/random\(\)/g, 'casioRandom()');
  t = t.replace(/randint\(([^,]+),([^)]+)\)/g, 'casioRandInt($1, $2)');

  // ── implicit multiplication before functions ──
  const funcNames = 'sqrt|log|sin|cos|tan|asin|acos|atan|asinh|acosh|atanh|nthRoot|log10|abs|pi|permutations|combinations|pol|rec|casioSum|casioIntegral|casioDeriv|casioRandom|casioRandInt';
  const implicitRe = new RegExp(`([0-9)])\\s*(${funcNames})`, 'g');
  t = t.replace(implicitRe, '$1*$2');
  t = t.replace(/\)\(/g, ')*(');

  // ── implicit multiplication: number followed by ( ──
  t = t.replace(/([0-9])(\()/g, '$1*$2');

  return t;
}

// ══════════════════════════════════════════════
//  Evaluate expression string
// ══════════════════════════════════════════════

export function evalExpr(exprStr, angleMode = ANGLE.DEG, vars = {}) {
  const scope = math.parser();

  // Set variables
  for (const [k, v] of Object.entries(vars)) {
    if (v !== null && v !== undefined) {
      scope.set(k, math.bignumber(v));
    }
  }

  // Configure trig for angle mode  
  let expr = exprStr;
  if (angleMode === ANGLE.DEG) {
    // Wrap trig arguments with degree→radian conversion
    expr = wrapTrigForDeg(expr);
  } else if (angleMode === ANGLE.GRAD) {
    expr = wrapTrigForGrad(expr);
  }
  // RAD mode: no wrapping needed

  const result = scope.evaluate(expr);
  return math.number(result);
}

function wrapTrigForDeg(expr) {
  // Replace sin(...) with sin((...) * pi/180) etc. but NOT asin/asinh etc.
  // This is tricky with nested parens — use a simpler approach:
  // math.js supports unit-aware angles, but it's simpler to do the conversion ourselves
  // We'll post-process: feed the raw expression and handle angle conversion at eval time
  return expr; // handled differently — see evalWithAngle below
}

function wrapTrigForGrad(expr) {
  return expr; // handled differently
}

/**
 * Full evaluation with angle mode support.
 * Creates a custom math.js scope with trig functions overridden for the angle mode.
 * Also registers: casioSum, casioIntegral, casioDeriv, nPr, nCr, pol, rec, random helpers.
 */
export function evalWithAngle(exprStr, angleMode = ANGLE.DEG, vars = {}) {
  // Create a scope with overridden trig functions
  const scope = {};
  for (const [k, v] of Object.entries(vars)) {
    if (v !== null && v !== undefined) {
      scope[k] = typeof v === 'number' ? math.bignumber(v) : v;
    }
  }

  // Build custom functions
  const toRad = (x) => {
    const n = math.number(x);
    if (angleMode === ANGLE.DEG) return math.bignumber(n * Math.PI / 180);
    if (angleMode === ANGLE.GRAD) return math.bignumber(n * Math.PI / 200);
    return x;
  };
  const fromRad = (x) => {
    const n = math.number(x);
    if (angleMode === ANGLE.DEG) return math.bignumber(n * 180 / Math.PI);
    if (angleMode === ANGLE.GRAD) return math.bignumber(n * 200 / Math.PI);
    return x;
  };

  // Import custom trig functions that respect angle mode
  const customMath = math.create();
  customMath.import({
    sin: function(x) { return math.sin(toRad(x)); },
    cos: function(x) { return math.cos(toRad(x)); },
    tan: function(x) {
      const r = toRad(x);
      if (Math.abs(math.number(math.cos(r))) < 1e-15) throw new Error('Math ERROR');
      return math.tan(r);
    },
    asin: function(x) { return fromRad(math.asin(x)); },
    acos: function(x) { return fromRad(math.acos(x)); },
    atan: function(x) { return fromRad(math.atan(x)); },
    log10: function(x) { return math.log10(x); },

    // ── Summation: casioSum(exprBodyValue, var, start, end) ──
    // Since we can't get the symbolic expr from math.js evaluation,
    // summation will be pre-expanded before evaluation.
    // This is a placeholder — actual summation is handled in evalSummation().
    casioSum: function(body, v, start, end) {
      // If called with numbers, body is already evaluated for a single point
      // This shouldn't normally be called — summation is handled specially
      return body;
    },

    // ── Numerical Integration (Simpson's rule) ──
    casioIntegral: function(body, a, b) {
      // Placeholder — handled by pre-processing
      return body;
    },

    // ── Numerical Derivative (central difference) ──
    casioDeriv: function(body, v, pt) {
      return body;
    },

    // ── Pol(x, y) → r (stores θ internally) ──
    pol: function(x, y) {
      const xn = math.number(x), yn = math.number(y);
      const r = Math.sqrt(xn * xn + yn * yn);
      let theta = Math.atan2(yn, xn);
      if (angleMode === ANGLE.DEG) theta = theta * 180 / Math.PI;
      else if (angleMode === ANGLE.GRAD) theta = theta * 200 / Math.PI;
      // Store θ for retrieval (attached to scope)
      scope.__polTheta = theta;
      return math.bignumber(r);
    },

    // ── Rec(r, θ) → x (stores y internally) ──
    rec: function(r, theta) {
      const rn = math.number(r);
      let tn = math.number(theta);
      if (angleMode === ANGLE.DEG) tn = tn * Math.PI / 180;
      else if (angleMode === ANGLE.GRAD) tn = tn * Math.PI / 200;
      const x = rn * Math.cos(tn);
      const y = rn * Math.sin(tn);
      scope.__recY = y;
      return math.bignumber(x);
    },

    // ── Casio random: 3-digit random 0.000–0.999 ──
    casioRandom: function() {
      return math.bignumber(Math.floor(Math.random() * 1000) / 1000);
    },

    // ── Random integer in range ──
    casioRandInt: function(a, b) {
      const lo = math.number(a), hi = math.number(b);
      return math.bignumber(Math.floor(Math.random() * (hi - lo + 1)) + lo);
    },
  }, { override: true });

  // ── Handle summation specially ──
  if (exprStr.includes('casioSum(')) {
    return evalSummation(exprStr, customMath, scope);
  }

  // ── Handle integral specially ──
  if (exprStr.includes('casioIntegral(')) {
    return evalIntegral(exprStr, customMath, scope);
  }

  // ── Handle derivative specially ──
  if (exprStr.includes('casioDeriv(')) {
    return evalDerivative(exprStr, customMath, scope);
  }

  const result = customMath.evaluate(exprStr, scope);

  // Convert to plain number
  if (math.isBigNumber(result)) return result.toNumber();
  if (typeof result === 'number') return result;
  return math.number(result);
}

// ── Summation evaluator ──
function evalSummation(exprStr, customMath, scope) {
  // Parse: casioSum(body, var, start, end) + possible surrounding expr
  const m = exprStr.match(/casioSum\((.+),\s*([a-zA-Z]),\s*([^,]+),\s*([^)]+)\)/);
  if (!m) throw new Error('Syntax ERROR');
  const [, bodyExpr, varName, startStr, endStr] = m;

  const start = Math.round(customMath.evaluate(startStr, { ...scope }).toNumber ? customMath.evaluate(startStr, { ...scope }).toNumber() : Number(customMath.evaluate(startStr, { ...scope })));
  const end = Math.round(customMath.evaluate(endStr, { ...scope }).toNumber ? customMath.evaluate(endStr, { ...scope }).toNumber() : Number(customMath.evaluate(endStr, { ...scope })));

  if (end - start > 10000) throw new Error('Math ERROR');

  let sum = 0;
  for (let i = start; i <= end; i++) {
    const iterScope = { ...scope, [varName]: customMath.bignumber(i) };
    const val = customMath.evaluate(bodyExpr, iterScope);
    sum += customMath.number(val);
  }

  // If there's surrounding expression, substitute
  const outer = exprStr.replace(/casioSum\(.+?\)/, String(sum));
  if (outer !== String(sum)) {
    const result = customMath.evaluate(outer, scope);
    return customMath.number(result);
  }
  return sum;
}

// ── Integral evaluator (Simpson's 3/8 composite rule) ──
function evalIntegral(exprStr, customMath, scope) {
  const m = exprStr.match(/casioIntegral\((.+),\s*([^,]+),\s*([^)]+)\)/);
  if (!m) throw new Error('Syntax ERROR');
  const [, bodyExpr, aStr, bStr] = m;

  const a = Number(customMath.evaluate(aStr, { ...scope }));
  const b = Number(customMath.evaluate(bStr, { ...scope }));
  const N = 1000; // number of intervals (must be divisible by 3)
  const h = (b - a) / N;

  const f = (xVal) => {
    const s = { ...scope, x: customMath.bignumber(xVal) };
    return Number(customMath.evaluate(bodyExpr, s));
  };

  // Simpson's 3/8 rule
  let sum = f(a) + f(b);
  for (let i = 1; i < N; i++) {
    const xi = a + i * h;
    sum += (i % 3 === 0 ? 2 : 3) * f(xi);
  }
  const result = sum * 3 * h / 8;

  const outer = exprStr.replace(/casioIntegral\(.+?\)/, String(result));
  if (outer !== String(result)) {
    return Number(customMath.evaluate(outer, scope));
  }
  return result;
}

// ── Derivative evaluator (central difference) ──
function evalDerivative(exprStr, customMath, scope) {
  const m = exprStr.match(/casioDeriv\((.+),\s*([a-zA-Z]),\s*([^)]+)\)/);
  if (!m) throw new Error('Syntax ERROR');
  const [, bodyExpr, varName, ptStr] = m;

  const pt = Number(customMath.evaluate(ptStr, { ...scope }));
  const h = 1e-6;

  const f = (xVal) => {
    const s = { ...scope, [varName]: customMath.bignumber(xVal) };
    return Number(customMath.evaluate(bodyExpr, s));
  };

  // 5-point stencil for better accuracy
  const result = (-f(pt + 2*h) + 8*f(pt + h) - 8*f(pt - h) + f(pt - 2*h)) / (12 * h);

  const outer = exprStr.replace(/casioDeriv\(.+?\)/, String(result));
  if (outer !== String(result)) {
    return Number(customMath.evaluate(outer, scope));
  }
  return result;
}

// ── DMS (Degrees, Minutes, Seconds) ──
export function decimalToDMS(decimal) {
  const sign = decimal < 0 ? '-' : '';
  const abs = Math.abs(decimal);
  const deg = Math.floor(abs);
  const minFull = (abs - deg) * 60;
  const min = Math.floor(minFull);
  const sec = (minFull - min) * 60;
  return { sign, deg, min, sec: Math.round(sec * 1000) / 1000 };
}

export function dmsToDecimal(deg, min, sec) {
  return deg + min / 60 + sec / 3600;
}

export function formatDMS(decimal) {
  const { sign, deg, min, sec } = decimalToDMS(decimal);
  return `${sign}${deg}°${min}'${sec}"`;
}

// ── Engineering notation ──
export function toEngineering(value, direction) {
  if (value === 0) return { mantissa: 0, exponent: 0 };
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  let exp = Math.floor(Math.log10(abs));
  // Round down to nearest multiple of 3
  exp = Math.floor(exp / 3) * 3;
  if (direction === 'right') exp -= 3; // ←ENG: shift decimal left = decrease exponent
  if (direction === 'left') exp += 3;  // ENG→: shift decimal right = increase exponent
  const mantissa = sign * abs / Math.pow(10, exp);
  return { mantissa, exponent: exp };
}

export function formatEngineering(value) {
  const { mantissa, exponent } = toEngineering(value);
  const symbols = { '-15': 'f', '-12': 'p', '-9': 'n', '-6': 'μ', '-3': 'm', '0': '', '3': 'k', '6': 'M', '9': 'G', '12': 'T', '15': 'P' };
  const sym = symbols[String(exponent)];
  if (sym !== undefined) {
    let m = mantissa.toPrecision(10).replace(/\.?0+$/, '');
    return `${m}${sym}`;
  }
  let m = mantissa.toPrecision(10).replace(/\.?0+$/, '');
  return `${m}×10^${exponent}`;
}

// ══════════════════════════════════════════════
//  Result formatting
// ══════════════════════════════════════════════

/**
 * Format a decimal number for display.
 * decimals: number of decimal places (0=auto, 8, 16)
 */
export function formatDecimal(value, precision = 10) {
  if (value === 0) return '0';
  if (!isFinite(value)) return 'Math ERROR';
  if (isNaN(value)) return 'Math ERROR';

  const absVal = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  if (absVal >= 1e10 || (absVal < 1e-2 && absVal !== 0)) {
    return formatSci(value, precision);
  }

  let str = absVal.toPrecision(precision);
  if (str.includes('.')) str = str.replace(/\.?0+$/, '');
  return sign + str;
}

function formatSci(value, precision = 10) {
  if (value === 0) return '0';
  const sign = value < 0 ? '-' : '';
  const absVal = Math.abs(value);
  const exp = Math.floor(Math.log10(absVal));
  const mantissa = absVal / Math.pow(10, exp);
  let mantStr = mantissa.toPrecision(precision);
  if (mantStr.includes('.')) mantStr = mantStr.replace(/\.?0+$/, '');
  return `${sign}${mantStr}×10^${exp}`;
}

// ── Surd simplification ──

function simpleSurd(value) {
  // value² to get the radicand
  const sq = value * value;
  if (Math.abs(sq) > 1e12 || !isFinite(sq)) return null;

  let fSq;
  try {
    fSq = new Fraction(sq).simplify(1e-12);
  } catch { return null; }

  // factor out perfect squares from numerator and denominator
  const factorOut = (n) => {
    if (n <= 0) return { outside: 0, inside: 1 };
    if (n === 1) return { outside: 1, inside: 1 };
    let outside = 1, inside = n;
    for (let r = Math.floor(Math.sqrt(n)); r >= 2; r--) {
      const sq = r * r;
      if (inside % sq === 0) {
        inside /= sq;
        outside *= r;
      }
    }
    return { outside, inside };
  };

  const numFact = factorOut(fSq.n);
  const denFact = factorOut(fSq.d);

  const combinedInside = numFact.inside * denFact.inside;
  const cFact = factorOut(combinedInside);

  let coeff = numFact.outside * cFact.outside;
  const radicand = cFact.inside;
  let denom = denFact.outside * denFact.inside;

  if (radicand === 1) return null; // it's rational, not a surd

  // Simplify coeff/denom
  const g = gcd(coeff, denom);
  coeff /= g;
  denom /= g;

  if (coeff >= 1000 || radicand >= 1000 || denom >= 1000) return null;

  return {
    type: 'surd',
    value: {
      sign: value < 0 ? '-' : '',
      numeratorCoefficient: coeff,
      radicand,
      denominator: denom,
    }
  };
}

function gcd(a, b) {
  a = Math.abs(Math.round(a));
  b = Math.abs(Math.round(b));
  while (b) { [a, b] = [b, a % b]; }
  return a;
}

// ── Pi fraction detection ──

function piResult(value) {
  if (value === 0 || !isFinite(value)) return null;

  const ratio = value / Math.PI;
  let f;
  try {
    f = new Fraction(ratio).simplify(1e-12);
  } catch { return null; }

  // Only show if numerator and denominator are small
  if (Math.abs(f.n) >= 1000 || f.d >= 1000) return null;

  // Verify accuracy
  const approx = f.s * f.n / f.d * Math.PI;
  if (Math.abs(approx - value) > 1e-10) return null;

  if (f.n === 0) return null;

  return {
    type: 'pi_fraction',
    value: { n: f.n, d: f.d, s: f.s }
  };
}

// ── Simple fraction detection ──

function fractionResult(value) {
  if (!isFinite(value) || isNaN(value)) return null;
  if (Number.isInteger(value)) return null;

  let f;
  try {
    f = new Fraction(value).simplify(1e-12);
  } catch { return null; }

  if (f.d === 1) return null;
  if (Math.abs(f.n) >= 1000 || f.d >= 1000) return null;

  // Verify
  const approx = f.s * f.n / f.d;
  if (Math.abs(approx - value) > 1e-9) return null;

  return {
    type: 'fraction',
    value: { n: f.n, d: f.d, s: f.s }
  };
}

// ── Mixed fraction ──

function mixedFractionResult(value) {
  if (!isFinite(value) || isNaN(value)) return null;
  if (Number.isInteger(value)) return null;

  let f;
  try {
    f = new Fraction(value).simplify(1e-12);
  } catch { return null; }

  if (f.d === 1) return null;
  if (f.n <= f.d) return null; // not improper → no mixed form

  const integer = Math.floor(f.n / f.d);
  const remainder = f.n % f.d;

  if (integer === 0 || remainder === 0) return null;

  return {
    type: 'mixed_fraction',
    value: { integer, n: remainder, d: f.d, s: f.s }
  };
}

/**
 * Determine the best "math" display for a result.
 * Returns a structured result object or a plain string.
 */
export function formatMathResult(value, forceDecimal = false, outputMode = 'math') {
  if (!isFinite(value) || isNaN(value)) return 'Math ERROR';
  if (Number.isInteger(value)) return formatDecimal(value);
  if (forceDecimal) {
    const precision = outputMode === 'decimal8' ? 8 : outputMode === 'decimal16' ? 16 : 10;
    return formatDecimal(value, precision);
  }

  // Try pi fraction
  const piFrac = piResult(value);
  if (piFrac) return piFrac;

  // Try surd
  const surd = simpleSurd(value);
  if (surd) return surd;

  // Try simple fraction
  const frac = fractionResult(value);
  if (frac) return frac;

  // Fallback to decimal
  return formatDecimal(value);
}

/**
 * Get S⇔D cycle for a value.
 * Returns array of possible display modes.
 */
export function getSDCycle(value) {
  const representations = [];

  // Mode 0: Math result (fraction/surd/pi)
  const mathResult = formatMathResult(value, false);
  if (typeof mathResult === 'object') {
    representations.push(mathResult);

    // If it's an improper fraction, add mixed fraction
    if (mathResult.type === 'fraction' && mathResult.value.n > mathResult.value.d) {
      const mixed = mixedFractionResult(value);
      if (mixed) representations.push(mixed);
    }
  }

  // Decimal mode
  representations.push(formatDecimal(value));

  return representations;
}

// Re-export for convenience
export { math, Fraction };

// ── Equation solver helpers (for Equation/Function mode) ──
export function solveSimultaneous(coeffMatrix, constVector) {
  try {
    const A = math.matrix(coeffMatrix);
    const b = math.matrix(constVector);
    const result = math.lusolve(A, b);
    return result.toArray().map(r => Array.isArray(r) ? r[0] : r).map(v => math.number(v));
  } catch {
    throw new Error('Math ERROR');
  }
}

export function solvePolynomial(coeffs) {
  // coeffs: [an, an-1, ..., a1, a0] for anx^n + ... + a0 = 0
  const degree = coeffs.length - 1;
  if (degree === 2) return solveQuadratic(coeffs[0], coeffs[1], coeffs[2]);
  if (degree === 3) return solveCubicNumeric(coeffs);
  if (degree === 4) return solveQuarticNumeric(coeffs);
  throw new Error('Argument ERROR');
}

function solveQuadratic(a, b, c) {
  const disc = b * b - 4 * a * c;
  if (disc >= 0) {
    const sq = Math.sqrt(disc);
    return [(-b + sq) / (2 * a), (-b - sq) / (2 * a)];
  }
  // Complex roots
  const real = -b / (2 * a);
  const imag = Math.sqrt(-disc) / (2 * a);
  return [
    { re: real, im: imag },
    { re: real, im: -imag },
  ];
}

function solveCubicNumeric(coeffs) {
  // Use companion matrix eigenvalues via math.js
  return solveByCompanion(coeffs);
}

function solveQuarticNumeric(coeffs) {
  return solveByCompanion(coeffs);
}

function solveByCompanion(coeffs) {
  const n = coeffs.length - 1;
  const lead = coeffs[0];
  // Build companion matrix
  const companion = [];
  for (let i = 0; i < n; i++) {
    const row = new Array(n).fill(0);
    if (i < n - 1) row[i + 1] = 1;
    row[0] = 0; // will be overwritten for last row
    companion.push(row);
  }
  // Last row: -a0/an, -a1/an, ..., -an-1/an
  for (let i = 0; i < n; i++) {
    companion[i][n - 1] = -coeffs[n - i] / lead;
    if (i < n - 1) {
      companion[i] = new Array(n).fill(0);
      companion[i][i + 1] = 1;
    }
  }
  // Actually, proper companion matrix:
  const comp = [];
  for (let i = 0; i < n; i++) {
    const row = new Array(n).fill(0);
    if (i > 0) row[i - 1] = 1;
    comp.push(row);
  }
  for (let i = 0; i < n; i++) {
    comp[i][n - 1] = -coeffs[n - i] / lead;
  }

  try {
    const result = math.eigs(math.matrix(comp));
    const eigenvalues = result.values.toArray();
    return eigenvalues.map(v => {
      if (typeof v === 'object' && v.im !== undefined) {
        return Math.abs(v.im) < 1e-10 ? v.re : { re: v.re, im: v.im };
      }
      return math.number(v);
    });
  } catch {
    // Fallback: numerical root finding with Durand-Kerner
    return durandKerner(coeffs);
  }
}

function durandKerner(coeffs) {
  const n = coeffs.length - 1;
  const lead = coeffs[0];
  let roots = [];
  for (let i = 0; i < n; i++) {
    const angle = (2 * Math.PI * i / n) + 0.4;
    roots.push({ re: 0.4 * Math.cos(angle), im: 0.4 * Math.sin(angle) });
  }

  const polyEval = (z) => {
    let result = { re: coeffs[0], im: 0 };
    for (let i = 1; i <= n; i++) {
      const t = { re: result.re * z.re - result.im * z.im, im: result.re * z.im + result.im * z.re };
      result = { re: t.re + coeffs[i], im: t.im };
    }
    return result;
  };

  const cDiv = (a, b) => {
    const d = b.re * b.re + b.im * b.im;
    if (d === 0) return { re: 0, im: 0 };
    return { re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d };
  };

  const cSub = (a, b) => ({ re: a.re - b.re, im: a.im - b.im });
  const cMul = (a, b) => ({ re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re });

  for (let iter = 0; iter < 1000; iter++) {
    let maxDelta = 0;
    for (let i = 0; i < n; i++) {
      let denom = { re: 1 / lead, im: 0 };
      for (let j = 0; j < n; j++) {
        if (j !== i) denom = cMul(denom, cSub(roots[i], roots[j]));
      }
      const delta = cDiv(polyEval(roots[i]), cMul({ re: lead, im: 0 }, denom));
      roots[i] = cSub(roots[i], delta);
      maxDelta = Math.max(maxDelta, Math.sqrt(delta.re * delta.re + delta.im * delta.im));
    }
    if (maxDelta < 1e-12) break;
  }

  return roots.map(r => Math.abs(r.im) < 1e-10 ? r.re : r);
}

// ── Inequality solver ──
export function solveInequality(coeffs, type) {
  // Find roots of polynomial
  const roots = solvePolynomial(coeffs).filter(r => typeof r === 'number').sort((a, b) => a - b);

  // Test sign in each interval
  const testPoints = [];
  if (roots.length === 0) {
    testPoints.push(0);
  } else {
    testPoints.push(roots[0] - 1);
    for (let i = 0; i < roots.length - 1; i++) {
      testPoints.push((roots[i] + roots[i + 1]) / 2);
    }
    testPoints.push(roots[roots.length - 1] + 1);
  }

  const polyEval = (x) => {
    let result = 0;
    const n = coeffs.length - 1;
    for (let i = 0; i <= n; i++) {
      result += coeffs[i] * Math.pow(x, n - i);
    }
    return result;
  };

  const intervals = [];
  const isLE = type === '<=' || type === '≤';
  const isLT = type === '<';
  const isGE = type === '>=' || type === '≥';
  const isGT = type === '>';

  const satisfies = (val) => {
    if (isLT) return val < 0;
    if (isLE) return val <= 0;
    if (isGT) return val > 0;
    if (isGE) return val >= 0;
    return false;
  };

  // Check each interval
  for (let i = 0; i < testPoints.length; i++) {
    const val = polyEval(testPoints[i]);
    if (satisfies(val)) {
      const lo = i === 0 ? null : roots[i - 1];
      const hi = i === roots.length ? null : roots[i];
      intervals.push({ lo, hi, loInclusive: isLE || isGE, hiInclusive: isLE || isGE });
    }
  }

  return { roots, intervals };
}
